import { cn } from '../utils';

/**
 * Renders a video URL as a STATIC poster — its first frame, no controls, no
 * autoplay. Use it wherever a "thumbnail" URL turns out to be a video file
 * (`.mp4` cover) that an `<img>` can't decode; pair it with a URL check like
 * `isVideoThumbnailUrl`. `preload="metadata"` paints the first frame without
 * downloading the whole clip; `muted` + `playsInline` keep it a quiet poster.
 *
 * Pure presentational (no hooks, no `'use client'`) — server-renderable. The
 * default `className` fills its (positioned) parent and crops to cover; pass a
 * `className` to override.
 */
export function VideoPoster({
  src,
  className,
  'aria-label': ariaLabel,
}: {
  src: string;
  className?: string;
  'aria-label'?: string;
}) {
  // Append a `#t=0.1` media fragment so the browser SEEKS to 0.1s and paints
  // that frame as the poster. Without it, `preload="metadata"` alone leaves a
  // blank/black frame in several browsers (the reported "still blank"). Skip if
  // the URL already carries a fragment.
  const posterSrc = src.includes('#') ? src : `${src}#t=0.1`;
  return (
    <video
      src={posterSrc}
      muted
      playsInline
      preload="metadata"
      aria-label={ariaLabel}
      className={cn('absolute inset-0 h-full w-full object-cover', className)}
    />
  );
}
